/**
 * Repairing what OCR mis-saw — carefully.
 *
 * The temptation is a big fuzzy-match table: anything within two edits of
 * "protein" becomes protein, `9` becomes `g`, and the label reads beautifully.
 * That is exactly how a scanner produces confident wrong answers, because the
 * same substitutions that fix `Prote1n` will happily turn `Iron` into `1ron`
 * into `100` and put it in the wrong column.
 *
 * So corrections here obey three rules:
 *
 *   1. Character substitutions are applied ONLY where the surrounding context
 *      says which alphabet a character belongs to — digits next to a unit,
 *      letters inside a word.
 *   2. A word is matched against nutrient vocabulary, not against everything.
 *      `Iron` is only considered as a nutrient name in a name position.
 *   3. Every correction is reported, so confidence can be reduced for it. A
 *      value we had to repair is not as good as one we read cleanly.
 */

import type { NutrientKey } from './types';

/**
 * Characters OCR confuses, split by which direction the fix runs.
 *
 * Kept separate on purpose: `0`->`O` and `O`->`0` are both real OCR errors, and
 * applying both blindly would let any token become any other.
 */
const TO_DIGIT: Record<string, string> = {
  O: '0', o: '0', Q: '0', D: '0',
  l: '1', I: '1', i: '1', '|': '1', '!': '1',
  Z: '2', z: '2',
  E: '3',
  A: '4',
  S: '5', s: '5',
  G: '6', b: '6',
  T: '7', '?': '7',
  B: '8',
  g: '9', q: '9',
};

const TO_LETTER: Record<string, string> = {
  '0': 'o', '1': 'l', '5': 's', '8': 'b', '9': 'g', '6': 'g', '2': 'z',
};

/** Units we accept, lower-cased and stripped of punctuation. */
export const UNITS = ['kcal', 'kj', 'mcg', 'µg', 'ug', 'mg', 'ml', 'l', 'g'] as const;
export type Unit = (typeof UNITS)[number];

/**
 * Unit spellings OCR actually produces.
 *
 * Only substitutions that cannot be a different valid unit are listed. `m9` can
 * only ever have been `mg`; `q` alone is ambiguous enough that it is handled in
 * context by `readUnit`, not here.
 */
const UNIT_FIXES: Record<string, Unit> = {
  m9: 'mg', mq: 'mg', rng: 'mg', mig: 'mg', mgs: 'mg',
  mc9: 'mcg', mcq: 'mcg', mog: 'mcg',
  kcaI: 'kcal', kcai: 'kcal', kca1: 'kcal', kcau: 'kcal', kcal_: 'kcal',
  kj: 'kj', kJ: 'kj', k1: 'kj',
  mI: 'ml', m1: 'ml', rnl: 'ml',
  '9': 'g', q: 'g',
};

export interface Normalized {
  text: string;
  /** True when a substitution was applied, so confidence can be lowered. */
  corrected: boolean;
}

/** Strip decoration OCR adds around real text, without touching the text. */
export function cleanToken(raw: string): string {
  return raw
    .replace(/[‘’“”]/g, '')
    .replace(/^[^\p{L}\p{N}<(.\-+]+/u, '')
    .replace(/[^\p{L}\p{N}%).]+$/u, '')
    .trim();
}

/**
 * Read a number, allowing the digit-shaped letters OCR produces.
 *
 * Returns null rather than guessing when the token still is not a number after
 * repair — an unparseable amount must stay missing, not become zero.
 */
export function readNumber(raw: string): { value: number; corrected: boolean } | null {
  const token = cleanToken(raw).replace(/^[<~≈]/, '');
  if (token.length === 0) return null;

  /*
   * A token needs at least one real digit before letter-to-digit repair runs.
   *
   * Without this, `D` on "Vitamin D 1.5mcg" became the number 0 — `D` is a
   * plausible mis-read of a zero, so the repair table maps it — and the row
   * was then parsed as "Vitamin" with an amount of nothing. Repair is for
   * damaged numbers, not for turning words into them.
   */
  if (!/[0-9]/.test(token)) return null;

  let corrected = false;
  let out = '';
  for (const ch of token) {
    if (/[0-9]/.test(ch)) {
      out += ch;
      continue;
    }
    if (ch === '.' || ch === ',') {
      // A comma is a decimal separator on most of the world's labels.
      out += '.';
      continue;
    }
    const digit = TO_DIGIT[ch];
    if (digit === undefined) return null;
    out += digit;
    corrected = true;
  }

  // Two separators means the token was never a single number.
  if ((out.match(/\./g) ?? []).length > 1) return null;
  const value = Number(out);
  if (!Number.isFinite(value)) return null;
  return { value, corrected };
}

/**
 * Read a unit from a token, using the number beside it as evidence.
 *
 * `hasNumber` is what disambiguates the genuinely ambiguous cases: a lone `9`
 * following an amount is the letter g; a `9` with no amount nearby is a digit
 * and must not be silently turned into a unit.
 */
export function readUnit(raw: string, hasNumber: boolean): { unit: Unit; corrected: boolean } | null {
  const token = cleanToken(raw).toLowerCase().replace(/[.]/g, '');
  if (token.length === 0) return null;

  for (const unit of UNITS) {
    if (token === unit) return { unit, corrected: false };
  }

  const fixed = UNIT_FIXES[token] ?? UNIT_FIXES[cleanToken(raw).replace(/[.]/g, '')];
  if (fixed !== undefined) {
    // `9` and `q` only become `g` when something numeric precedes them.
    if ((token === '9' || token === 'q') && !hasNumber) return null;
    return { unit: fixed, corrected: true };
  }
  return null;
}

/**
 * Nutrient vocabulary.
 *
 * Aliases cover the genuine variation between countries and the abbreviations
 * labels actually print — NOT OCR damage, which is handled by the edit-distance
 * match below. Ordered so that longer, more specific names are tested first:
 * "saturated fat" must not be consumed by the "fat" entry.
 */
const VOCABULARY: Array<{ key: NutrientKey; names: string[] }> = [
  { key: 'saturatedFat', names: ['saturated fat', 'saturates', 'saturated fatty acids', 'sat fat', 'of which saturates', 'saturated'] },
  { key: 'transFat', names: ['trans fat', 'trans fatty acids', 'trans'] },
  { key: 'totalFat', names: ['total fat', 'fat total', 'fat', 'total lipid'] },
  { key: 'dietaryFiber', names: ['dietary fibre', 'dietary fiber', 'fibre', 'fiber', 'roughage'] },
  { key: 'addedSugars', names: ['added sugars', 'added sugar', 'includes added sugars'] },
  { key: 'totalSugars', names: ['total sugars', 'sugars', 'sugar', 'of which sugars'] },
  { key: 'totalCarbohydrate', names: ['total carbohydrate', 'carbohydrate total', 'carbohydrate', 'carbohydrates', 'carbs', 'total carbs'] },
  { key: 'cholesterol', names: ['cholesterol'] },
  { key: 'sodium', names: ['sodium', 'salt'] },
  { key: 'protein', names: ['protein', 'proteins'] },
  { key: 'calories', names: ['calories', 'energy', 'calorie', 'kilojoules'] },
  { key: 'vitaminD', names: ['vitamin d', 'vit d'] },
  { key: 'calcium', names: ['calcium'] },
  { key: 'iron', names: ['iron'] },
  { key: 'potassium', names: ['potassium'] },
];

/** Nutrients real labels print that this app does not model. */
const UNMODELLED = [
  'polyunsaturated fat',
  'monounsaturated fat',
  'polyunsaturates',
  'monounsaturates',
  'not a significant source',
];

/** Damerau-Levenshtein, capped: past the cap the answer does not matter. */
export function editDistance(a: string, b: string, cap = 3): number {
  if (Math.abs(a.length - b.length) > cap) return cap + 1;
  const prev = new Array<number>(b.length + 1);
  const cur = new Array<number>(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    cur[0] = i;
    let rowMin = cur[0]!;
    for (let j = 1; j <= b.length; j++) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      cur[j] = Math.min(cur[j - 1]! + 1, prev[j]! + 1, prev[j - 1]! + cost);
      if (cur[j]! < rowMin) rowMin = cur[j]!;
    }
    if (rowMin > cap) return cap + 1;
    for (let j = 0; j <= b.length; j++) prev[j] = cur[j]!;
  }
  return prev[b.length]!;
}

export interface NutrientMatch {
  key: NutrientKey;
  /** 0..1: exact spelling is 1, a repaired one is lower. */
  score: number;
  matched: string;
}

/**
 * Match a row's label text to a nutrient.
 *
 * Two-stage on purpose. An exact substring match is trusted outright. Only if
 * that fails does edit distance run, and then the tolerance scales with word
 * length — one wrong character in "iron" is a much weaker signal than one wrong
 * character in "carbohydrate", because there are far more four-letter words it
 * could have been.
 */
export function matchNutrient(rawLabel: string): NutrientMatch | null {
  const text = rawLabel
    .toLowerCase()
    .replace(/[^a-z\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (text.length < 3) return null;

  for (const entry of VOCABULARY) {
    for (const name of entry.names) {
      if (text === name) return { key: entry.key, score: 1, matched: name };
    }
  }
  /*
   * Names we deliberately recognise in order to IGNORE them.
   *
   * "Polyunsaturated Fat" contains "saturated fat", and "Monounsaturated Fat"
   * contains "fat". Left to the vocabulary below, a US panel's two extra fat
   * rows silently overwrite the real total and saturated figures — and on a
   * label where the values differ, the reader is shown a number from the wrong
   * row. We model neither nutrient, so skipping them is the honest outcome.
   */
  for (const ignored of UNMODELLED) {
    if (new RegExp(`\b${ignored}\b`).test(text)) return null;
  }

  /*
   * Substring, but only at a word boundary.
   *
   * A bare `includes` match is what let "polyunsaturated fat" be read as
   * saturated fat: the substring is present, but it does not start a word.
   */
  for (const entry of VOCABULARY) {
    for (const name of entry.names) {
      if (new RegExp(`\b${name}\b`).test(text)) {
        return { key: entry.key, score: 0.92, matched: name };
      }
    }
  }

  let best: NutrientMatch | null = null;
  for (const entry of VOCABULARY) {
    for (const name of entry.names) {
      // Roughly one substitution per five characters, never more than three.
      const tolerance = Math.min(3, Math.max(1, Math.floor(name.length / 5)));
      const d = editDistance(text, name, tolerance);
      if (d > tolerance) continue;
      const score = 0.85 - d * 0.12;
      if (best === null || score > best.score) best = { key: entry.key, score, matched: name };
    }
  }
  return best;
}

/** Repair letters inside a word that OCR rendered as digits. */
export function repairWord(raw: string): Normalized {
  let corrected = false;
  let out = '';
  for (const ch of raw) {
    if (/[a-zA-Z\s]/.test(ch)) {
      out += ch;
      continue;
    }
    const letter = TO_LETTER[ch];
    if (letter === undefined) {
      out += ch;
      continue;
    }
    out += letter;
    corrected = true;
  }
  return { text: out, corrected };
}

/** True when the token is a percent daily value rather than an amount. */
export function isPercent(raw: string): boolean {
  return /%$/.test(cleanToken(raw)) || /^\d{1,3}\s*%$/.test(raw.trim());
}
